import React, { useState, useRef } from "react";
import { Alert } from "antd";
import { WorkspaceProps } from "@/components/UI/TableToolbar/WorkspaceButton/workspace";
import { WorkspaceModal } from "@/components/UI/TableToolbar/WorkspaceButton/SaveWorkspaceButton/WorkspaceModal";
import { WorkspaceConfig } from "@/types/workspaceConfig";

const validateImportedConfig = (data: unknown): data is WorkspaceConfig => {
  if (!data || typeof data !== "object" || Array.isArray(data)) return false;
  const config = data as Record<string, unknown>;
  return (
    (config.global === null || typeof config.global === "object") &&
    Array.isArray(config.tabs)
  );
};

type ImportWorkspaceButtonProps = {
  onConfigLoaded: (config: WorkspaceConfig) => void;
};

export const ImportWorkspaceButton = ({
  onConfigLoaded,
}: ImportWorkspaceButtonProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [importError, setImportError] = useState<string | null>(null);

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    event.target.value = "";

    if (!file.name.endsWith(".json")) {
      setImportError("Please select a .json file.");
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const config = JSON.parse(e.target?.result as string);
        if (validateImportedConfig(config)) {
          setImportError(null);
          onConfigLoaded(config);
        } else {
          setImportError(
            "Invalid JSON file. Please select a valid workspace configuration.",
          );
        }
      } catch {
        setImportError(
          "Invalid JSON file. Please select a valid workspace configuration.",
        );
      }
    };
    reader.onerror = () => {
      setImportError("Failed to read file. Please try again.");
    };
    reader.readAsText(file);
  };

  return (
    <>
      <button
        className="w-full flex flex-row gap-2 py-1 items-center justify-start hover:bg-[#ECFAFF]"
        onClick={() => {
          setImportError(null);
          fileInputRef.current?.click();
        }}
      >
        Import Workspace
      </button>

      {importError && (
        <Alert
          title={importError}
          type="error"
          showIcon
          closable
          onClose={() => setImportError(null)}
          className="mt-2 w-full"
        />
      )}

      <input
        type="file"
        ref={fileInputRef}
        accept=".json"
        style={{ display: "none" }}
        onChange={handleFileUpload}
      />
    </>
  );
};

type ImportWorkspaceModalProps = {
  workspace: WorkspaceProps;
  importedConfig: WorkspaceConfig | null;
  onClose: () => void;
  onImportComplete?: () => void;
};

export const ImportWorkspaceModal = ({
  workspace,
  importedConfig,
  onClose,
  onImportComplete,
}: ImportWorkspaceModalProps) => {
  const handleConfirm = async (name: string, isVivoTemplate?: boolean) => {
    if (!importedConfig) return;

    const newWorkspace = await workspace.mutations.createWorkspaceAsync({
      title: name,
      table_name: workspace.tableName,
      configuration: importedConfig,
      is_vivo_template: isVivoTemplate,
    });
    workspace.setCurrentWorkspace(newWorkspace.id);
    onClose();
    onImportComplete?.();
  };

  return (
    <WorkspaceModal
      isOpen={importedConfig !== null}
      onCancel={onClose}
      onConfirm={handleConfirm}
      defaultValue="Imported Workspace"
      existingNames={workspace.workspaces.map((w) => w.title)}
    />
  );
};
